import { useDebounceFn } from '@vueuse/core'
import { toValue, type MaybeRefOrGetter } from 'vue'
import { emitter } from '@/utils/eventBus'
import type {
	IRequestResponse,
	PaginationContext,
	PaginationMeta,
	PaginationQuery,
	RecordSchema,
	TableFilterOptions
} from '@/types'

export type RecordsFn<T> = (
	params: RecordSchema
) => Promise<IRequestResponse<PaginationContext<T[]>> | PaginationContext<T[]> | Blob | null>

interface CacheEntry {
	data: PaginationContext<unknown>
	timestamp: number
}

type CacheStore = Record<string, Record<string, Record<number, CacheEntry>>>

const attachSn = <T extends Record<string, unknown>>(
	items: T[],
	currentPage: number,
	perPage: number
): (T & { key: number })[] =>
	items.map((item, index) => ({
		...item,
		key: index + 1 + (currentPage - 1) * perPage
	}))

/**
 * Normalizes the response from a RecordsFn.
 * Handles both IRequestResponse wrapped data and direct PaginationContext.
 */
const normalizeResponse = <T>(
	response: IRequestResponse<PaginationContext<T[]>> | PaginationContext<T[]> | Blob | null
): PaginationContext<T[]> | null => {
	if (!response || response instanceof Blob) return null

	// If it has a 'data' property and looks like IRequestResponse
	if ('data' in response && response.data && typeof response.data === 'object' && 'items' in response.data) {
		return response.data as PaginationContext<T[]>
	}

	// Direct PaginationContext
	if ('items' in response) {
		return response as PaginationContext<T[]>
	}

	return null
}

export const usePaginator = <T extends RecordSchema>(props: {
	records: RecordsFn<T>
	options?: ComputedRef<TableFilterOptions | null | undefined>
	cacheKey?: MaybeRefOrGetter<string | undefined>
	transformRecord?: (record: T) => T
	onLoad?: (data: PaginationContext<T>) => void
}) => {
	const route = useRoute()
	const router = useRouter()
	const limit = shallowRef(30)

	const paginatorCache = useState<CacheStore>('paginatorCache', () => ({}))

	const paginationMeta = ref<Required<PaginationMeta>>({
		path: '',
		per_page: 30,
		query: '',
		filter: '',
		total_pages: 0,
		total: 0,
		total_count: 0
	})
	const paginationQuery = ref<PaginationQuery>({
		current_page: 1,
		limit: 30,
		search: null
	})

	const tableRecord = ref<(T & { key: number })[]>([]) as Ref<(T & { key: number })[]>

	const currentPage = computed<number>(() => {
		const p = parseInt(route.query?.page as string, 10)
		return isNaN(p) || p < 1 ? 1 : p
	})

	const options = computed(() => props.options?.value ?? null)

	const stableFilterKey = computed(() => {
		const filter = (options.value?.filter ?? {}) as Record<string, unknown>
		const watch = options.value?.watch ?? null
		const queryToIgnore = options.value?.ignoreWatchingFilter ?? []

		const sortedFilter = Object.keys(filter)
			.filter(key => filter[key] !== undefined && filter[key] !== null && filter[key] !== '')
			.filter(key => !queryToIgnore.includes(key))
			.sort()
			.reduce((acc: Record<string, unknown>, key) => {
				acc[key] = filter[key]
				return acc
			}, {})

		return JSON.stringify({ filter: sortedFilter, watch })
	})

	const queryKey = computed(() => {
		const perPage = limit.value || paginationQuery.value.limit
		return `${stableFilterKey.value}-${perPage}`
	})

	const isFetching = shallowRef(false)
	const fetchError = shallowRef<Error | null>(null)

	const networkKey = computed(() => {
		const providedKey = toValue(props.cacheKey)

		if (providedKey && providedKey !== 'default') {
			return providedKey
		}

		// Fallback to route name + params for isolation
		const routeName = String(route.name || 'unknown-route')
		const routeParams = JSON.stringify(route.params)
		const fallbackKey = `${routeName}:${routeParams}`

		// Developer warning for multiple tables on same page
		if (import.meta.dev && options.value?.cache) {
			console.warn(
				`[usePaginator] No unique cacheKey provided for table on route "${routeName}". ` +
					`Falling back to route-based key: "${fallbackKey}". ` +
					'If this page has multiple tables, they will COLLIDE. Please provide an explicit cacheKey.'
			)
		}

		return fallbackKey
	})

	const setPaginationMeta = (response: PaginationMeta) => {
		paginationMeta.value = {
			per_page: response?.per_page || 30,
			total_pages: response?.total_pages || 0,
			total: response?.total || 0,
			path: response?.path || '',
			query: response?.query || '',
			filter: response?.filter || '',
			total_count: response?.total_count || 0
		}
	}

	const fetchPage = async (page: number = currentPage.value, force = false) => {
		if (!props.records) return

		const currentNetworkKey = networkKey.value
		const ttl = options.value?.cache ?? 2 * 60 * 1000
		const currentQueryKey = queryKey.value

		// Check for cached response
		if (!force && paginatorCache.value[currentNetworkKey]?.[currentQueryKey]?.[page]) {
			const entry = paginatorCache.value[currentNetworkKey][currentQueryKey][page]
			if (Date.now() - entry.timestamp < ttl) {
				const response = entry.data
				let rawItems = options.value?.transformResponse
					? (options.value.transformResponse(response.items as Record<string, unknown>[]) as unknown as T[])
					: (response.items as T[])

				if (props.transformRecord) {
					rawItems = rawItems.map(props.transformRecord)
				}

				tableRecord.value = attachSn(rawItems, page, Number(response.per_page || 30))
				paginationMeta.value = {
					per_page: response?.per_page || 30,
					total_pages: response?.total_pages || 0,
					total: response?.total || 0,
					path: response?.path || '',
					query: response?.query || '',
					filter: '',
					total_count: response?.total_count || 0
				}
				paginationQuery.value = {
					current_page: Number(page),
					limit: Number(response.per_page || 30),
					search: response.search
				}
				props.onLoad?.(response as unknown as PaginationContext<T>)
				return
			}
		}

		isFetching.value = true
		fetchError.value = null

		try {
			const perPage = limit.value || paginationMeta.value.per_page

			const rawResponse = await props.records({
				page,
				limit: perPage,
				...(options.value?.filter ?? {})
			})

			const response = normalizeResponse<T>(rawResponse)
			if (!response) {
				// If it's a blob, we don't handle it here but we reset loading
				isFetching.value = false
				return
			}

			if (!paginatorCache.value[currentNetworkKey]) paginatorCache.value[currentNetworkKey] = {}
			if (!paginatorCache.value[currentNetworkKey][currentQueryKey])
				paginatorCache.value[currentNetworkKey][currentQueryKey] = {}

			paginatorCache.value[currentNetworkKey][currentQueryKey][page] = {
				data: response,
				timestamp: Date.now()
			}

			let rawItems = options.value?.transformResponse
				? (options.value.transformResponse(response.items) as unknown as T[])
				: (response.items as T[])

			if (props.transformRecord) {
				rawItems = rawItems.map(props.transformRecord)
			}

			tableRecord.value = attachSn(rawItems, page, Number(response.per_page || 30))
			setPaginationMeta(response)
			paginationQuery.value = {
				current_page: Number(page),
				limit: Number(response.per_page || 30),
				search: response.search
			}
			props.onLoad?.(response as unknown as PaginationContext<T>)
		} catch (e) {
			fetchError.value = e instanceof Error ? e : new Error(String(e))
			console.error('[usePaginator] fetch failed:', fetchError.value)
		} finally {
			isFetching.value = false
		}
	}

	const switchPages = async (page: number) => {
		await router.replace({
			query: { ...route.query, page: page === 1 ? undefined : page }
		})
		await fetchPage(page)
	}

	const refreshKey = computed(() => `paginator:refresh:${networkKey.value}`)

	const invalidateCache = (key = networkKey.value) => {
		if (paginatorCache.value[key]) {
			paginatorCache.value[key] = {}
		}
	}

	const refreshRequest = () => {
		invalidateCache()
		return fetchPage(currentPage.value, true)
	}

	// Filter / watch options changed → reset to page 1 and refetch
	const debouncedFetch = useDebounceFn(() => {
		router.replace({ query: { ...route.query, page: undefined } })
		fetchPage(1)
	}, 500)

	watch(stableFilterKey, (next, prev) => {
		if (next !== prev) {
			debouncedFetch()
		}
	})

	watch(limit, (next, prev) => {
		if (next !== prev && prev !== 0) {
			fetchPage(currentPage.value)
		}
	})

	// Invalidate/Re-fetch when networkKey changes (e.g. plan changed)
	watch(networkKey, (next, prev) => {
		if (next !== prev) {
			fetchPage(currentPage.value)
		}
	})

	onMounted(() => {
		emitter.on(refreshKey.value, refreshRequest)
		fetchPage(currentPage.value)
	})

	onUnmounted(() => {
		emitter.off(refreshKey.value, refreshRequest)
	})

	return {
		paginationQuery,
		paginationMeta,
		invalidateCache,
		tableRecord,
		currentPage,
		isFetching,
		fetchError,
		limit,
		fetchPage,
		switchPages,
		refreshRequest,
		refreshKey
	}
}

export const invalidateTableCache = (key = 'default') => {
	const cache = useState<CacheStore>('paginatorCache')
	if (cache.value?.[key]) {
		cache.value[key] = {}
	}
}

export const refreshTable = (key = 'default') => {
	invalidateTableCache(key)
	emitter.emit(`paginator:refresh:${key}`)
}
